import { useCallback, useEffect, useRef, useState } from 'react';

import { Modal } from 'flowbite';

import { modalOptions } from 'Helpers/modalOptions';

export default function useModal({ options = modalOptions(false), visible } = {}) {
    const [element, setElement] = useState(null);
    const [isVisible, setIsVisible] = useState(visible);

    const ref = useRef();
    const initializedTargetRef = useRef(null);

    useEffect(() => {
        // Only build a flowbite instance when the target node changes. The deps
        // array sees `undefined` on the first render (the ref attaches at commit),
        // so without this guard the effect re-runs after setElement() and creates
        // a SECOND instance for the same node — and a modal shown before the
        // second instance settles leaks the first instance's backdrop on close.
        if (ref.current && initializedTargetRef.current !== ref.current) {
            initializedTargetRef.current = ref.current;
            setElement(
                new Modal(ref.current, {
                    ...options,
                    onHide: () => {
                        typeof options.onHide === 'function' && options.onHide();
                        setIsVisible(false);
                    },
                    onShow: () => {
                        typeof options.onShow === 'function' && options.onShow();
                        setIsVisible(true);
                    },
                }),
            );
        }
    }, [ref.current]);

    useEffect(() => {
        element && (isVisible ? element.show() : element.hide());
    }, [element, isVisible]);

    const show = useCallback(() => {
        setIsVisible(true);
    }, [element]);

    const hide = useCallback(() => {
        setIsVisible(false);
    }, [element]);

    return { show, hide, visible: isVisible, element, ref };
}
